import React, { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import * as signalR from '@microsoft/signalr';
import { getLocalStorage } from '../../utils/helper';

interface SignalRContextType {
    connection: signalR.HubConnection | null;
    messages: string[];
}

const SignalRContext = createContext<SignalRContextType | undefined>(undefined);

interface SignalRProviderProps {
    children: ReactNode;
}

export const SignalRProvider: React.FC<SignalRProviderProps> = ({ children }) => {
    const connection = useRef<signalR.HubConnection | null>(null);
    const [messages, setMessages] = useState<string[]>([]);

    useEffect(() => {
        const websocketUrl = process.env.REACT_APP_WEBSOCKET_URL;
        if (!websocketUrl) {
            console.error("Missing REACT_APP_WEBSOCKET_URL environment variable");
            return;
        }
        connection.current = new signalR.HubConnectionBuilder()
            .withUrl(websocketUrl, {
                accessTokenFactory: () => getLocalStorage('accessToken') || '',
                withCredentials: true,
            })
            .withAutomaticReconnect()
            .build();

        connection.current
            .start()
            .then(() => console.log('SignalR Connected'))
            .catch(err => console.error('SignalR Connection Error: ', err));

        connection.current.on('ReceiveMessage', (message: string) => {
            console.log('new message', JSON.parse(message));

            setMessages(prev => [...prev, message]);
        });

        return () => {
            connection.current?.stop();
        };
    }, []);

    return (
        <SignalRContext.Provider value={{ connection: connection.current, messages }}>
            {children}
        </SignalRContext.Provider>
    );
};

export const useSignalR = () => {
    const context = useContext(SignalRContext);
    if (!context) throw new Error('useSignalR must be used within a SignalRProvider');
    return context;
};
